import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Plus, RefreshCw, AlertCircle, CheckCircle, Search, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

// Enhanced Property Creation & Management Dashboard Component
const PropertyCreationManagementDashboard = () => {
  // State management
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  
  // Enhanced form state with validation
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    price: '',
    property_type: 'apartment',
    status: 'available',
    bedrooms: '',
    bathrooms: '',
    surface_area: '',
    description: '',
    commune_id: null,
    agent_id: null,
    owner_id: null,
    features: {},
    images: [],
    videos: []
  });

  // Options for dropdowns
  const [communes, setCommunes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [owners, setOwners] = useState([]);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Statistics state
  const [statistics, setStatistics] = useState({
    total: 0,
    available: 0,
    reserved: 0,
    sold: 0,
    recent: 0
  });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Enhanced data loading function
  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProperties(),
        fetchOptions(),
        fetchStatistics()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setFormErrors({ general: 'Erreur lors du chargement des données' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch properties with enhanced error handling
  const fetchProperties = async () => {
    try {
      const { data, error } = await supabase
        ?.from('properties')
        ?.select(`
          *,
          commune:communes(name, district),
          agent:user_profiles!agent_id(full_name, email),
          owner:user_profiles!owner_id(full_name, email)
        `)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Property fetch error:', error);
        throw error;
      }

      console.log(`✅ Propriétés chargées: ${data?.length || 0}`, data);
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setFormErrors({ general: 'Erreur lors du chargement des propriétés' });
    }
  };

  // Fixed dropdown options fetch
  const fetchOptions = async () => {
    try {
      const [communesRes, agentsRes, ownersRes] = await Promise.all([
        supabase?.from('communes')?.select('id, name, district')?.order('name'),
        supabase?.from('user_profiles')?.select('id, full_name, email')?.eq('role', 'agent')?.order('full_name'),
        supabase?.from('user_profiles')?.select('id, full_name, email')?.eq('role', 'client')?.order('full_name')
      ]);

      // Format data for Select component
      const formattedCommunes = (communesRes?.data || [])?.map(commune => ({
        value: commune?.id,
        label: `${commune?.name} (${commune?.district})`
      }));

      const formattedAgents = (agentsRes?.data || [])?.map(agent => ({
        value: agent?.id,
        label: `${agent?.full_name} (${agent?.email})`
      }));

      const formattedOwners = (ownersRes?.data || [])?.map(owner => ({
        value: owner?.id,
        label: `${owner?.full_name} (${owner?.email})`
      }));

      setCommunes(formattedCommunes);
      setAgents(formattedAgents);
      setOwners(formattedOwners);

      console.log('✅ Options loaded:', { 
        communes: formattedCommunes?.length, 
        agents: formattedAgents?.length, 
        owners: formattedOwners?.length 
      });
    } catch (error) {
      console.error('Error fetching options:', error);
      setFormErrors({ general: 'Erreur lors du chargement des options de sélection' });
    }
  };

  // Calculate statistics
  const fetchStatistics = async () => {
    try {
      const { data, error } = await supabase?.from('properties')?.select('status, created_at');
      
      if (error) throw error;

      const today = new Date();
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      const stats = {
        total: data?.length || 0,
        available: data?.filter(p => p?.status === 'available')?.length || 0,
        reserved: data?.filter(p => p?.status === 'reserved')?.length || 0,
        sold: data?.filter(p => p?.status === 'sold')?.length || 0,
        recent: data?.filter(p => new Date(p?.created_at) > sevenDaysAgo)?.length || 0
      };

      setStatistics(stats);
    } catch (error) {
      console.error('Error calculating statistics:', error);
    }
  };

  // Enhanced form validation
  const validateForm = () => {
    const errors = {};

    // Required field validation
    if (!formData?.title?.trim()) errors.title = 'Le titre est obligatoire';
    if (!formData?.address?.trim()) errors.address = 'L\'adresse est obligatoire';
    if (!formData?.price || parseFloat(formData?.price) <= 0) {
      errors.price = 'Le prix doit être supérieur à 0';
    }

    // Numeric validation
    if (formData?.bedrooms && (isNaN(formData?.bedrooms) || parseInt(formData?.bedrooms) < 0)) {
      errors.bedrooms = 'Nombre de chambres invalide';
    }
    if (formData?.bathrooms && (isNaN(formData?.bathrooms) || parseInt(formData?.bathrooms) < 0)) {
      errors.bathrooms = 'Nombre de salles de bain invalide';
    }
    if (formData?.surface_area && (isNaN(formData?.surface_area) || parseFloat(formData?.surface_area) <= 0)) {
      errors.surface_area = 'Surface invalide';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  // Enhanced property creation with better error handling
  const handleCreateProperty = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      console.log('Validation errors:', formErrors);
      return;
    }

    setCreateLoading(true);
    setFormErrors({});
    setSuccessMessage('');

    try {
      // Prepare property data with proper type conversion and UUID validation
      const propertyData = {
        title: formData?.title?.trim(),
        address: formData?.address?.trim(),
        description: formData?.description?.trim() || null,
        price: parseFloat(formData?.price),
        property_type: formData?.property_type,
        status: formData?.status,
        bedrooms: formData?.bedrooms ? parseInt(formData?.bedrooms) : null,
        bathrooms: formData?.bathrooms ? parseInt(formData?.bathrooms) : null,
        surface_area: formData?.surface_area ? parseFloat(formData?.surface_area) : null,
        // Enhanced UUID field handling - ensure only valid UUIDs or null are sent
        commune_id: formData?.commune_id && formData?.commune_id !== '' ? formData?.commune_id : null,
        agent_id: formData?.agent_id && formData?.agent_id !== '' ? formData?.agent_id : null,
        owner_id: formData?.owner_id && formData?.owner_id !== '' ? formData?.owner_id : null,
        features: formData?.features || {},
        images: formData?.images || [],
        videos: formData?.videos || []
      };

      console.log('🏠 Création de propriété:', propertyData);

      const { data, error } = await supabase
        ?.from('properties')
        ?.insert(propertyData)
        ?.select(`
          *,
          commune:communes(name, district),
          agent:user_profiles!agent_id(full_name, email),
          owner:user_profiles!owner_id(full_name, email)
        `);

      if (error) {
        console.error('Creation error:', error);
        throw error;
      }

      console.log('✅ Propriété créée avec succès:', data);

      // Reset form and refresh data
      setFormData({
        title: '',
        address: '',
        price: '',
        property_type: 'apartment',
        status: 'available',
        bedrooms: '',
        bathrooms: '',
        surface_area: '',
        description: '',
        commune_id: null,
        agent_id: null,
        owner_id: null,
        features: {},
        images: [],
        videos: []
      });

      setSuccessMessage(`Propriété "${data?.[0]?.title}" créée avec succès !`);
      setShowCreateForm(false);

      // Refresh data
      await Promise.all([fetchProperties(), fetchStatistics()]);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);

    } catch (error) {
      console.error('Property creation error:', error);
      setFormErrors({ 
        general: error?.message || 'Erreur lors de la création de la propriété' 
      });
    } finally {
      setCreateLoading(false);
    }
  };

  // Handle feature toggle
  const handleFeatureChange = (feature, checked) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev?.features,
        [feature]: checked
      }
    }));
  };

  // Filter properties based on search and filters
  const filteredProperties = properties?.filter(property => {
    const matchesSearch = !searchTerm || 
      property?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      property?.address?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      property?.commune?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesStatus = filterStatus === 'all' || property?.status === filterStatus;
    const matchesType = filterType === 'all' || property?.property_type === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Add property type and status options for dropdowns
  const propertyTypeOptions = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'villa', label: 'Villa' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const statusOptions = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  const filterStatusOptions = [
    { value: 'all', label: 'Tous statuts' },
    ...statusOptions
  ];

  const filterTypeOptions = [
    { value: 'all', label: 'Tous types' },
    ...propertyTypeOptions
  ];

  return (
    <>
      <Helmet>
        <title>Création & Gestion des Propriétés - Tableau de Bord Avancé</title>
        <meta name="description" content="Interface complète pour la création et gestion des propriétés immobilières avec workflow optimisé" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Enhanced Header with Actions */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Création & Gestion des Propriétés
                </h1>
                <p className="text-gray-600">
                  Interface complète avec création intégrée, validation en temps réel et suivi des performances
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => loadInitialData()}
                  variant="outline"
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
                
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Nouvelle Propriété
                </Button>
              </div>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-green-800">{successMessage}</span>
              </div>
            )}

            {formErrors?.general && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-red-800">{formErrors?.general}</span>
              </div>
            )}
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics?.total}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <div className="w-6 h-6 bg-blue-600 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Disponibles</p>
                  <p className="text-2xl font-bold text-green-600">{statistics?.available}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-full">
                  <div className="w-6 h-6 bg-green-600 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Réservées</p>
                  <p className="text-2xl font-bold text-yellow-600">{statistics?.reserved}</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-full">
                  <div className="w-6 h-6 bg-yellow-600 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Vendues</p>
                  <p className="text-2xl font-bold text-red-600">{statistics?.sold}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-full">
                  <div className="w-6 h-6 bg-red-600 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Récentes (7j)</p>
                  <p className="text-2xl font-bold text-purple-600">{statistics?.recent}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <div className="w-6 h-6 bg-purple-600 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Creation Form Modal */}
          {showCreateForm && (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
              <div className="flex min-h-screen items-center justify-center p-4">
                <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
                  {/* Form Header */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">Nouvelle Propriété</h2>
                        <p className="text-sm text-gray-500">Créez une nouvelle propriété avec validation en temps réel</p>
                      </div>
                      <Button
                        onClick={() => setShowCreateForm(false)}
                        variant="outline"
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </Button>
                    </div>
                  </div>

                  {/* Enhanced Form with Fixed Dropdowns */}
                  <form onSubmit={handleCreateProperty} className="p-6 space-y-6">
                    {/* Basic Information Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Informations de Base
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Titre de la propriété *
                          </label>
                          <Input
                            value={formData?.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
                            placeholder="Ex: Villa moderne avec piscine"
                            className={formErrors?.title ? 'border-red-500' : ''}
                          />
                          {formErrors?.title && (
                            <p className="text-sm text-red-600 mt-1">{formErrors?.title}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Adresse *
                          </label>
                          <Input
                            value={formData?.address}
                            onChange={(e) => setFormData(prev => ({ ...prev, address: e?.target?.value }))}
                            placeholder="Adresse complète de la propriété"
                            className={formErrors?.address ? 'border-red-500' : ''}
                          />
                          {formErrors?.address && (
                            <p className="text-sm text-red-600 mt-1">{formErrors?.address}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Prix (FCFA) *
                          </label>
                          <Input
                            type="number"
                            value={formData?.price}
                            onChange={(e) => setFormData(prev => ({ ...prev, price: e?.target?.value }))}
                            placeholder="Prix en FCFA"
                            min="0"
                            className={formErrors?.price ? 'border-red-500' : ''}
                          />
                          {formErrors?.price && (
                            <p className="text-sm text-red-600 mt-1">{formErrors?.price}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type de propriété *
                          </label>
                          <Select
                            value={formData?.property_type}
                            onChange={(value) => setFormData(prev => ({ ...prev, property_type: value }))}
                            options={propertyTypeOptions}
                            placeholder="Sélectionner un type"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Statut
                          </label>
                          <Select
                            value={formData?.status}
                            onChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                            options={statusOptions}
                            placeholder="Sélectionner un statut"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Commune
                          </label>
                          <Select
                            value={formData?.commune_id}
                            onChange={(value) => setFormData(prev => ({ ...prev, commune_id: value }))}
                            options={communes}
                            placeholder="Sélectionner une commune"
                            searchable
                            loading={communes?.length === 0}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Property Details Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Détails de la Propriété
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Chambres
                          </label>
                          <Input
                            type="number"
                            value={formData?.bedrooms}
                            onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: e?.target?.value }))}
                            min="0"
                            className={formErrors?.bedrooms ? 'border-red-500' : ''}
                          />
                          {formErrors?.bedrooms && (
                            <p className="text-sm text-red-600 mt-1">{formErrors?.bedrooms}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Salles de bain
                          </label>
                          <Input
                            type="number"
                            value={formData?.bathrooms}
                            onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: e?.target?.value }))}
                            min="0"
                            className={formErrors?.bathrooms ? 'border-red-500' : ''}
                          />
                          {formErrors?.bathrooms && (
                            <p className="text-sm text-red-600 mt-1">{formErrors?.bathrooms}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Surface (m²)
                          </label>
                          <Input
                            type="number"
                            value={formData?.surface_area}
                            onChange={(e) => setFormData(prev => ({ ...prev, surface_area: e?.target?.value }))}
                            min="0"
                            step="0.1"
                            className={formErrors?.surface_area ? 'border-red-500' : ''}
                          />
                          {formErrors?.surface_area && (
                            <p className="text-sm text-red-600 mt-1">{formErrors?.surface_area}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Features Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Caractéristiques
                      </h3>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { key: 'pool', label: 'Piscine' },
                          { key: 'garden', label: 'Jardin' },
                          { key: 'parking', label: 'Parking' },
                          { key: 'garage', label: 'Garage' },
                          { key: 'balcony', label: 'Balcon' },
                          { key: 'elevator', label: 'Ascenseur' },
                          { key: 'furnished', label: 'Meublé' },
                          { key: 'security', label: 'Sécurité' }
                        ]?.map(feature => (
                          <label key={feature?.key} className="flex items-center p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={formData?.features?.[feature?.key] || false}
                              onChange={(e) => handleFeatureChange(feature?.key, e?.target?.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            />
                            <span className="text-sm text-gray-700">{feature?.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Assignment Section - Fixed */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Attribution
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Agent assigné
                          </label>
                          <Select
                            value={formData?.agent_id}
                            onChange={(value) => setFormData(prev => ({ ...prev, agent_id: value }))}
                            options={agents}
                            placeholder="Sélectionner un agent"
                            searchable
                            clearable
                            loading={agents?.length === 0}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Propriétaire
                          </label>
                          <Select
                            value={formData?.owner_id}
                            onChange={(value) => setFormData(prev => ({ ...prev, owner_id: value }))}
                            options={owners}
                            placeholder="Sélectionner un propriétaire"
                            searchable
                            clearable
                            loading={owners?.length === 0}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Description Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Description
                      </h3>
                      
                      <textarea
                        value={formData?.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Description détaillée de la propriété, ses avantages, son environnement..."
                      />
                    </div>

                    {/* Form Actions */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -m-6 mt-6 rounded-b-lg">
                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowCreateForm(false)}
                          disabled={createLoading}
                        >
                          Annuler
                        </Button>
                        
                        <Button
                          type="submit"
                          disabled={createLoading}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        >
                          {createLoading ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          {createLoading ? 'Création en cours...' : 'Créer la Propriété'}
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Filters and Search - Fixed */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    placeholder="Rechercher par titre, adresse ou commune..."
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <Select
                  value={filterStatus}
                  onChange={(value) => setFilterStatus(value)}
                  options={filterStatusOptions}
                  className="w-40"
                />

                <Select
                  value={filterType}
                  onChange={(value) => setFilterType(value)}
                  options={filterTypeOptions}
                  className="w-40"
                />
              </div>
            </div>
          </div>

          {/* Enhanced Properties Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Liste des Propriétés ({filteredProperties?.length || 0})
              </h3>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
                <p className="text-gray-500">Chargement des propriétés...</p>
              </div>
            ) : filteredProperties?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Propriété
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prix
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type/Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commune
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Détails
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Création
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProperties?.map(property => (
                      <tr key={property?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {property?.title}
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {property?.address}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {new Intl.NumberFormat('fr-FR', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })?.format(property?.price)} FCFA
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              {property?.property_type}
                            </span>
                            <div>
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                property?.status === 'available' ? 'bg-green-100 text-green-800' :
                                property?.status === 'reserved' ? 'bg-yellow-100 text-yellow-800' :
                                property?.status === 'sold'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {property?.status}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {property?.commune?.name || '-'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {property?.commune?.district || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {property?.bedrooms ? `${property?.bedrooms} ch.` : ''} 
                            {property?.bathrooms ? ` • ${property?.bathrooms} sdb.` : ''}
                          </div>
                          <div className="text-sm text-gray-500">
                            {property?.surface_area ? `${property?.surface_area} m²` : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {new Date(property?.created_at)?.toLocaleDateString('fr-FR')}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(property?.created_at)?.toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plus className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterStatus !== 'all' || filterType !== 'all' ?'Aucune propriété ne correspond aux critères de recherche' :'Aucune propriété créée pour le moment'
                  }
                </p>
                {!searchTerm && filterStatus === 'all' && filterType === 'all' && (
                  <Button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Créer votre première propriété
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Creation History Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Historique de Création Récente
            </h3>
            
            <div className="space-y-3">
              {properties
                ?.slice(0, 5)
                ?.map(property => (
                  <div key={property?.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {property?.title}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(property?.created_at)?.toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
                        Succès
                      </span>
                    </div>
                  </div>
                ))}
                
              {properties?.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun historique disponible
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyCreationManagementDashboard;